import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as yup from 'yup';
import _ from 'lodash';
import { useFormik, FormikProvider } from 'formik';
import * as chanceApi from '../../../lib/api/chance';
import {
  chanceEstimateActive,
  chanceOrderCount,
  setChance,
  viewChance,
} from '../../../modules/chanceModule/chance';
import DetailTemplate from '../../../components/common/template/DetailTemplate';
import CostCalcLockContainer from '../costcalc/CostCalcLockContainer';
import CostCalcListForm from '../../../components/chancemng/costcalc/CostCalcListForm';
import { computeRowAmounts } from '../../../components/chancemng/costcalc/costCalcShared';
import AskDialog from '../../../components/common/dialog/AskDialog';
import { listChanceCosts } from '../../../modules/chanceModule/chanceCosts';
import ChanceDetailForm from '../../../components/chancemng/detail/ChanceDetailForm';
import { getBaseCodes } from '../../../modules/auth';
import useFieldConfig from '../../../hooks/useFieldConfig';

const ChanceNeedsAnanlysisFormContainer = ({ stepperRef }) => {
  const arrayHelperRef = useRef();
  // 옵션 B(원가 산출 표 리뉴얼) - "원가산출 확정" 전에 CostCalcListForm이 표에 입력 오류가
  // 있는지 검사해 첫 오류 줄을 선택(패널 열기)할 수 있게 노출하는 API.
  const costCalcApiRef = useRef();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // 필드 설정 조회
  const { isFieldEnabled, isFieldRequired } = useFieldConfig('CHANCE');
  const { baseCodes, user, chance, costs } = useSelector(
    ({ auth, chanceModule }) => ({
      baseCodes: auth.baseCodes,
      user: auth.user,
      chance: chanceModule.chance.chance,
      costs: chanceModule.chanceCosts.costs,
    }),
  );

  const [itemCdOptions, setItemCdOptions] = useState([]);
  const [visible, setVisible] = useState(false);
  const onCancle = () => {
    setVisible(false);
  };
  const onConfirm = () => {
    setVisible(false);
  };
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [message, setMessage] = useState(
    '기본사항 등록이 완료되지 못했습니다.',
  );

  const checkValidation = () => {
    let valid = true;
    //원가산출 체크
    // if (formik.values.costCalcs?.length === 0) {
    //   setMessage(
    //     '원가 산출 제품을 추가하지 않았습니다. 원가 산출 내역을 확인하세요.',
    //   );
    //   valid = false;
    // }

    return valid;
  };

  //id가 unquie한것만 선택.
  const onSelectedProducts = (data) => {
    const products = [...selectedProducts, ...data];
    const filteredProducts = _.uniqBy(products, 'id');

    filteredProducts.forEach((product) => {
      arrayHelperRef.current.push({
        expUuid: product.expUuid ? product.expUuid : '',
        masterTyp: 'N',
        priceUuid: product.priceUuid,
        sellPartnerUuid: product.partnerUuid,
        sellPartnerNm: product.partnerNm,
        partnerUuid: product.partnerUuid,
        partnerNm: product.partnerNm,
        productNm: product.productNm,
        productUuid: product.productUuid,
        expCnt: 1,
        unitVal: product.unitVal,
        priceVal: product.priceVal,
        productDesc: product.productDesc,
        productUnit: product.productUnit,
        itemCd: product.itemCd,
      });
    });
  };

  const handleExtProduct = () => {
    arrayHelperRef.current.push({
      masterTyp: 'E',
      priceUuid: null,
      sellPartnerUuid: '',
      sellPartnerNm: '',
      partnerUuid: null,
      partnerNm: null,
      productNm: '',
      productUuid: null,
      expCnt: 1,
      unitVal: 0,
      priceVal: 0,
      productDesc: '',
      productUnit: '',
      itemCd: '기타',
    });
  };

  const [initialValues, setInitialValues] = useState({
    cstNm: '',
    // partnerInc: '',
    prjTyp: '',
    incTyp: '',
    // solutionTyp: '',
    // chanceTyp: '',
    prjImpt: '',
    bugetYn: '',
    partnerTyp: '',
    procurement: false,
    chargeNm: '',
    chargeEml: '',
    chargeTel: '',
    forcastTxt: '',
    productYn: '',
    customerUuid: '',
    customerNm: '',
    vendorCmp: '',
    vendorCmpNm: '',
    // vendorEmp: '',
    expDay: '',
    ordrEstDate: '',
    crtUsr: user?.usrUuid,
    crtNm: user?.usrNm,
    costCalcs: [],
  });

  // 필드 설정 기반 동적 validationSchema
  const validationSchema = useMemo(() => {
    const schema = {};

    const fieldValidations = {
      cstNm:      { base: yup.string(),                                          requiredMsg: '사업명을 입력하세요' },
      prjTyp:     { base: yup.string(),                                          requiredMsg: '프로젝트 타입을 선택하세요' },
      incTyp:     { base: yup.string().nullable(),                               requiredMsg: '거래처 유형을 선택하세요' },
      prjImpt:    { base: yup.string().nullable(),                               requiredMsg: '프로젝트 중요도를 선택하세요' },
      bugetYn:    { base: yup.string().nullable(),                               requiredMsg: '예산여부를 선택하세요' },
      productYn:  { base: yup.string().nullable(),                               requiredMsg: '제품여부를 선택하세요' },
      chargeNm:   { base: yup.string().nullable(),                               requiredMsg: '고객 담당자를 입력하세요' },
      chargeEml:  { base: yup.string().email('이메일 형식을 확인하세요').nullable(), requiredMsg: '이메일을 입력하세요' },
      chargeTel:  { base: yup.string().nullable(),                               requiredMsg: '연락처를 입력하세요' },
      forcastTxt: { base: yup.string().nullable(),                               requiredMsg: '참고사항을 입력하세요' },
      expDay: {
        base: yup.date()
          .transform((value, originalValue) => originalValue === '' ? null : value)
          .nullable()
          .typeError('날짜 형식을 확인하세요'),
        requiredMsg: '매출(수금)예정일을 선택하세요',
      },
    };

    Object.keys(fieldValidations).forEach((fieldCd) => {
      if (isFieldEnabled(fieldCd)) {
        const { base, requiredMsg } = fieldValidations[fieldCd];
        schema[fieldCd] = isFieldRequired(fieldCd) ? base.required(requiredMsg) : base;
      }
    });

    // 원가산출 항목 validation: 원가 미확정 상태일 때만 적용
    // costYn === 'Y'(확정) 상태에서는 수정 불가하므로 검사 제외
    if (chance?.costYn !== 'Y') {
      schema.costCalcs = yup.array().of(
        yup.object().shape({
          sellPartnerNm: yup.string('').nullable().required('매입처를 선택하세요'),
          expCnt:        yup.number().nullable().required('수량을 입력하세요'),
          unitVal:       yup.number().nullable().required('매출단가를 입력하세요'),
          priceVal:      yup.number().nullable().required('매입단가를 입력하세요'),
        }),
      );
    }

    return yup.object(schema);
  }, [isFieldEnabled, isFieldRequired, chance?.costYn]);

  // 컴포넌트 마운트 시 서버에서 최신 chance 데이터 재조회
  useEffect(() => {
    if (chance?.prjUuid) {
      dispatch(viewChance(chance.prjUuid));
    }
  }, []);

  useEffect(() => {
    if (!baseCodes) {
      dispatch(getBaseCodes());
    } else {
      const filteredList = _.filter(baseCodes, {
        blcCd: 'BASE',
        mlcCd: 'PRODUCT',
        slcCd: 'CATEGORY',
        useYn: 'Y',
      }).map((item) => ({
        ...item,
        step: item.step !== '' && item.step !== null ? parseInt(item.step) : 0,
      }));
      //console.log(filteredList, filterObject);
      const orderedLiist = _.orderBy(filteredList, 'step');

      setItemCdOptions(orderedLiist);
    }
  }, [baseCodes]);

  useEffect(() => {
    if (chance) {
      dispatch(listChanceCosts(chance.prjUuid));
    }
  }, [chance]);

  useEffect(() => {
    if (costs) {
      const copyChance = {
        ...chance,
        procurement: chance?.procurement === 'Y' ? true : false,
        // 매출처가 없으면 고객사 정보로 기본값 설정
        vendorCmp: chance?.vendorCmp || chance?.customerUuid || '',
        vendorCmpNm: chance?.vendorCmpNm || chance?.customerNm || '',
      };
      const copyCosts = _.clone(costs);
      const init = { ...copyChance, costCalcs: copyCosts };
      setInitialValues(init);
    }
  }, [costs]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const valid = checkValidation();

      if (!valid) {
        setVisible(true);
        return;
      } else {
        setSubmitting(true);
        onSave(values);
        formik.resetForm();
        setSubmitting(false);
      }

      stepperRef.current?.scrollIntoView({ behavior: 'smooth' }); //저장 후 stepper로 이동함.
    },
  });

  const onSave = async (values) => {
    let dataToSubmit = {
      prjUuid: chance.prjUuid,
      // partnerInc: values.partnerInc,
      cstNm: values.cstNm.trim(),
      prjTyp: values.prjTyp,
      //chanceTyp: values.chanceTyp,
      prjImpt: values.prjImpt,
      bugetYn: values.bugetYn,
      // partnerTyp: values.partnerTyp,
      chargeNm: values.chargeNm,
      chargeEml: values.chargeEml,
      chargeTel: values.chargeTel,
      forcastTxt: values.forcastTxt,
      incTyp: values.incTyp,
      // solutionTyp: values.solutionTyp,
      productYn: values.productYn,
      procurement: values?.procurement ? 'Y' : 'N',
      vendorCmp: values.vendorCmp,
      // vendorEmp: values.vendorEmp,
      customerUuid: values.customerUuid,
      crtUsr: values.crtUsr,
      expDay: values.expDay ? dayjs(values?.expDay).format('YYYY-MM-DD') : null,
      // 계약 예정일은 화면(ChanceDetailForm)에는 있는데 저장 값에서 빠져 있어서, 고르고 저장해도
      // 서버로 가지 않았다(2026-10-06 운영: "계약 예정일이 계속 저장되지 않습니다").
      ordrEstDate: values.ordrEstDate ? dayjs(values.ordrEstDate).format('YYYY-MM-DD') : null,
    };

    //console.log(dataToSubmit, 'dataToSubmit');

    try {
      const res = await chanceApi.editChance(dataToSubmit);
      if (res.data.info) {
        // 원가 확정 상태가 아닐 때만 원가 항목도 함께 저장
        if (chance?.costYn !== 'Y') {
          const calcData = onMakeCaclData();
          const calcRes = await chanceApi.editCosts(chance.prjUuid, calcData);
          if (calcRes.data) {
            dispatch(listChanceCosts(chance.prjUuid));
          }
        }
        dispatch(setChance(res.data.info));
        enqueueSnackbar(`저장 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };
  const onMakeCaclData = () => {
    return formik.values.costCalcs.map((item) => {
      // [버그수정, 차단 1] item.saleVal/item.buyVal은 옵션 B 포팅(9bda371) 이후 더 이상
      // 렌더 중 채워지지 않는다(순수 computeRowAmounts로 뺐기 때문) - 여기서 그 값을 읽으면
      // 새 행은 NaN, 수정된 행은 옛 값으로 저장됐다. computeRowAmounts로 매 제출 시 다시
      // 계산한다.
      const { saleVal, buyVal, profit, profitRate } = computeRowAmounts(item);

      return {
        expUuid: item.expUuid, //ID
        masterTyp: item.masterTyp, //기타, 일반항목 구분
        sellPartnerUuid: item.sellPartnerUuid, //매입사
        partnerUuid: item.partnerUuid, //제조사
        priceUuid: item.priceUuid,
        priceVal: item.priceVal,
        unitVal: item.unitVal,
        productNm: item.productNm,
        productUuid: item.productUuid,
        productDesc: item.productDesc,
        productUnit: item.productUnit,
        itemCd: item.itemCd, //아이템
        expCnt: item.expCnt,
        saleVal: saleVal,
        buyVal: buyVal,
        saleProfit: profit,
        salePct: profitRate,
      };
    });
  };

  // 합계(매출/매입/이익/이익율)는 CostCalcListForm이 표 안 합계 행에서 직접 계산해 보여준다
  // (옵션 B 표 리뉴얼 전엔 여기서 따로 계산해 표 위 CostSummary에 내려줬었다).

  //원가산출 확정/ 해제
  const [lockMessage, setLockMessage] = useState(false);
  const handleLockOpen = () => setLockMessage(true);
  const handleLockClose = () => {
    setLockMessage(false);
  };
  const onCostLock = async () => {
    // 옵션 B - 표에 입력 오류(필수값 누락 등)가 있으면 확정 API를 부르기 전에 막고, 그
    // 줄을 선택해 패널을 열어 보여준다(CostCalcListForm의 ApiRef 참고).
    if (costCalcApiRef.current && !costCalcApiRef.current.validateAndFocusFirstInvalid()) {
      enqueueSnackbar('원가산출 항목 중 입력값을 확인해야 하는 품목이 있습니다.', {
        variant: 'warning',
      });
      return;
    }

    const valid = checkValidation();

    if (valid) {
      try {
        // 1) 저장 먼저
        const saveRes = await chanceApi.editChance({
          prjUuid: chance.prjUuid,
          cstNm: formik.values.cstNm?.trim(),
          prjTyp: formik.values.prjTyp,
          prjImpt: formik.values.prjImpt,
          bugetYn: formik.values.bugetYn,
          chargeNm: formik.values.chargeNm,
          chargeEml: formik.values.chargeEml,
          chargeTel: formik.values.chargeTel,
          forcastTxt: formik.values.forcastTxt,
          incTyp: formik.values.incTyp,
          productYn: formik.values.productYn,
          procurement: formik.values.procurement ? 'Y' : 'N',
          vendorCmp: formik.values.vendorCmp,
          customerUuid: formik.values.customerUuid,
          crtUsr: formik.values.crtUsr,
          expDay: formik.values.expDay ? dayjs(formik.values.expDay).format('YYYY-MM-DD') : null,
          ordrEstDate: formik.values.ordrEstDate ? dayjs(formik.values.ordrEstDate).format('YYYY-MM-DD') : null,
        });

        if (!saveRes.data.info) {
          enqueueSnackbar('기본사항 저장에 실패했습니다.', { variant: 'error' });
          return;
        }

        // 2) 원가 산출 내역 저장
        const calcData = onMakeCaclData();
        const calcRes = await chanceApi.editCosts(chance.prjUuid, calcData);

        if (!calcRes.data) {
          enqueueSnackbar('원가산출 저장에 실패했습니다.', { variant: 'error' });
          return;
        }

        // 3) 원가 확정
        const res = await chanceApi.completeCost(chance.prjUuid);
        if (res.data.result === 'SUCCESS') {
          dispatch(setChance(res.data.info));
          dispatch(listChanceCosts(chance.prjUuid));
          enqueueSnackbar('원가산출이 확정되었습니다', { variant: 'success' });
        } else {
          const message =
            res.data.result === 'NOT_VALID_COST_INFO'
              ? '원가산출을 확정할 수 없습니다(마이너스 원가)'
              : '원가산출 항목을 추가해주세요';
          enqueueSnackbar(message, { variant: 'error' });
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }
    } else {
      handleLockOpen();
    }
  };

  const [unLockVisible, setUnlockVisible] = useState(false);
  const onUnLockCancle = () => {
    setUnlockVisible(false);
  };
  const onUnLockConfirm = () => {
    setUnlockVisible(false);
    onCostUnLock();
  };
  const onCostUnLock = async () => {
    try {
      const res = await chanceApi.unDoCost(chance.prjUuid);

      if (res.data.info) {
        dispatch(setChance(res.data.info));
        dispatch(chanceEstimateActive(chance.prjUuid));
        dispatch(chanceOrderCount(chance.prjUuid));
        enqueueSnackbar('원가산출이 해제되었습니다.', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
    >
      {/* <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        {chance?.costYn === 'Y' ? (
          <Button
            variant="contained"
            color="warning"
            onClick={() => setUnlockVisible(true)}
          >
            원가산출 해제
          </Button>
        ) : (
          <Button variant="contained" color="warning" onClick={onCostLock}>
            원가산출 확정
          </Button>
        )}

        <Button type="submit" variant="outlined" disabled={formik.isSubmitting}>
          저장
        </Button>
      </Box> */}

      {chance?.useYn === 'N' && (
        <DetailTemplate title="포캐스팅 실패 사유">
          <Box sx={{ whiteSpace: 'pre-line' }}>{chance?.failTxt}</Box>
        </DetailTemplate>
      )}

      <DetailTemplate title="포캐스팅 등록">
        <ChanceDetailForm formik={formik} chance={chance} isFieldEnabled={isFieldEnabled} isFieldRequired={isFieldRequired} />
      </DetailTemplate>

      <Box sx={{ my: 2 }}>
        {chance && chance?.costYn === 'Y' ? (
          <CostCalcLockContainer chance={chance} />
        ) : (
          <FormikProvider value={formik}>
            <CostCalcListForm
              formik={formik}
              ArrayHelperRef={arrayHelperRef}
              ApiRef={costCalcApiRef}
              selectedVendor={formik.values.vendorCmp}
              itemCdOptions={itemCdOptions}
              onSelectedProducts={onSelectedProducts}
              onAddEtcProduct={handleExtProduct}
              prjUuid={chance?.prjUuid}
            />
          </FormikProvider>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        {chance?.costYn === 'Y' ? (
          <Button
            variant="contained"
            color="warning"
            onClick={() => setUnlockVisible(true)}
          >
            원가산출 해제
          </Button>
        ) : (
          <Button variant="contained" color="warning" onClick={onCostLock}>
            원가산출 확정
          </Button>
        )}

        <Button
          type="button"
          variant="outlined"
          disabled={formik.isSubmitting}
          onClick={async () => {
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
              const toTouched = (obj) => {
                if (typeof obj === 'string') return true;
                if (Array.isArray(obj)) return obj.map(item => item ? toTouched(item) : undefined);
                if (obj && typeof obj === 'object') return Object.keys(obj).reduce((acc, key) => { acc[key] = toTouched(obj[key]); return acc; }, {});
                return true;
              };
              formik.setTouched(toTouched(errors), false);
              const messages = [];
              const collect = (obj) => {
                if (typeof obj === 'string') messages.push(obj);
                else if (Array.isArray(obj)) obj.forEach(item => item && collect(item));
                else if (obj && typeof obj === 'object') Object.values(obj).forEach(collect);
              };
              collect(errors);
              const unique = [...new Set(messages)];
              enqueueSnackbar(
                `입력하지 않은 항목이 있습니다.\n${unique.join(', ')}`,
                { variant: 'warning', style: { whiteSpace: 'pre-line' } },
              );
              return;
            }
            formik.handleSubmit();
          }}
        >
          저장
        </Button>
      </Box>
      <AskDialog
        visible={lockMessage}
        title="원가산출 확정 확인"
        description={`원가산출내역에 입력되지 않은 항목이 있습니다\r\n 원가산출을 재확인하세요`}
        confirmText="닫기"
        onConfirm={handleLockClose}
        onCancle={handleLockClose}
      />
      <AskDialog
        visible={unLockVisible}
        title="프로젝트 원가 산출 해제"
        description={`원가산출을 해제하시겠습니까?\r\n작업된(견적서 / 수주품의서 ) 등의 사항을 다시 작성해야됩니다.`}
        confirmText="해제"
        onConfirm={onUnLockConfirm}
        onCancle={onUnLockCancle}
      />
      <AskDialog
        visible={visible}
        title="등록 오류"
        description={message}
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </Box>
  );
};

export default ChanceNeedsAnanlysisFormContainer;
